import { BotData, EditBotsRequest } from "@shared/types";
import { BotFormType } from "@shared/features/bots/BotForm/common/types";
import useValidateBots from "@shared/features/bots/BotForm/common/useValidateBots";
import { useSetFormValues } from "@shared/features/bots/BotForm/common/hooks";
import useBulkEditBotFormValues from "@shared/features/bots/BotForm/common/hooks/useBulkEditBotFormValues";
import { useContext, useEffect, useState } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { getBaseParsedFormData } from "@shared/features/bots/BotForm/common/utils";
import useBotsRestoreChanges from "@shared/features/bots/BotForm/common/hooks/useBotsRestoreChanges";
import { empty } from "@shared/features/bots/BotForm/common/constants";
import useBulkActionAccountsOverlapData from "@shared/features/bots/BotForm/common/hooks/useBulkActionAccountsOverlapData";

const useBulkEditBotsForm = () => {
  const {
    formValues,
    botType,
    initialized: botFormInitialized,
    botFormType,
    checkedFields,
    overrideFields,
    getBotsDataStatus,
    getExchangeToSymbolPricesStatus,
  } = useContext(BotFormContext)!;
  const [initialized, setInitialized] = useState(false);
  const isBulkEdit = botFormType === BotFormType.BULK_EDIT;
  const finalInitialized = isBulkEdit ? initialized : botFormInitialized;
  const botsData = getBotsDataStatus.currentData?.data as BotData[] | undefined;
  const exchangeToSymbolPrices = getExchangeToSymbolPricesStatus.data;

  const {
    initialized: bulkEditInitialized,
    originalBotValues,
    originalCheckedFields,
  } = useBulkEditBotFormValues({
    botsData,
  });

  const {
    bulkActionBotsData,
    bulkActionInvalidBotsData,
    bulkActionValidBotsData,
  } = useValidateBots({
    botsData,
    exchangeToSymbolPrices,
  });
  const setFormValues = useSetFormValues();

  const { tabToRestoreChangesData } = useBotsRestoreChanges({
    setFormValues,
    originalBotValues,
    originalCheckedFields,
  });

  const { accountsOverlapData } = useBulkActionAccountsOverlapData({
    botsData,
  });

  useEffect(() => {
    if (botFormInitialized && bulkEditInitialized && !initialized) {
      setFormValues(originalBotValues);
      setInitialized(true);
    }
  }, [botFormInitialized, bulkEditInitialized, formValues]);

  const getParsedBulkEditFormData = () => {
    if (bulkActionBotsData) {
      return {
        ...getBaseParsedFormData(
          formValues,
          botType,
          checkedFields,
          overrideFields
        ),
        bot_ids: bulkActionValidBotsData?.map((bot) =>
          "id" in bot ? bot.id : undefined
        ),
        status: empty,
      } as EditBotsRequest;
    }
  };

  return {
    initialized: finalInitialized,
    botsData,
    originalBotValues,
    tabToRestoreChangesData: isBulkEdit ? tabToRestoreChangesData : undefined,
    bulkActionBotsData,
    getParsedBulkEditFormData,
    bulkActionValidBotsData,
    bulkActionInvalidBotsData,
    accountsOverlapData,
  };
};

export default useBulkEditBotsForm;
